import type { ReactNode } from 'react';
import { CHANGE_KINDS, type BriefAsset, type BriefDetails } from '@mybusinessmate/schema';
import { formatDay, formatDayYear } from '../../lib/project-display';
import { api } from '../../lib/api';
import { mapResponseError } from '@mybusinessmate/api-client';
import {
  CalendarBlank,
  FileText,
  ImageSquare,
  Link as LinkIcon,
  Sparkle,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { Icon } from '../Icon';
import { useToast } from '../ui/toast';

const CHANGE_LABEL: Record<(typeof CHANGE_KINDS)[number], string> = {
  text_prices: 'Text & prices',
  images: 'Images',
  layout: 'Layout',
  new_section: 'New section',
  fix: 'Fix something broken',
  other: 'Something else',
};

const PLATFORM: Record<string, string> = { instagram: 'Instagram', facebook: 'Facebook' };
const whenDay = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const whenTime = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
/** "Thu 23 Oct · 7:30 PM" (Figma A05.4). */
export const when = { format: (d: Date) => `${whenDay.format(d)} · ${whenTime.format(d)}` };

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-(--mbm-space-1)">
      <dt className="text-label-small text-text-tertiary uppercase">{label}</dt>
      <dd className="whitespace-pre-wrap text-body-medium text-text-primary">{children}</dd>
    </div>
  );
}

/** A field that is left out entirely when the brief did not fill it in. */
export function Maybe({ label, children }: { label: string; children: ReactNode }) {
  return children ? <Field label={label}>{children}</Field> : null;
}

/** Figma A05.4: the words the client will read (caption, script) sit on a grey panel under a small label. */
export function CopyPanel({ label, children, tags }: { label: string; children: ReactNode; tags?: string[] }) {
  if (!children && !tags?.length) return null;
  return (
    <div className="flex flex-col gap-(--mbm-space-2) rounded-(--mbm-radius-md) bg-surface-field p-(--mbm-space-4)">
      <span className="text-caption font-semibold tracking-wide text-text-secondary uppercase">{label}</span>
      {children ? <p className="whitespace-pre-wrap text-body-medium text-text-primary">{children}</p> : null}
      {tags?.length ? (
        <p className="text-body-medium text-brand-primary">{tags.map((t) => `#${t}`).join(' ')}</p>
      ) : null}
    </div>
  );
}

/** The practical facts of a post or reel in one quiet row: when, what format, which sound, where. */
export function FactRow({ items }: { items: { icon: PhosphorIcon; text: string | null | undefined }[] }) {
  const shown = items.filter((i): i is { icon: PhosphorIcon; text: string } => !!i.text);
  if (shown.length === 0) return null;
  return (
    <ul className="flex flex-wrap items-center gap-x-(--mbm-space-5) gap-y-(--mbm-space-2) text-body-small text-text-secondary">
      {shown.map((item) => (
        <li key={item.text} className="inline-flex items-center gap-(--mbm-space-2)">
          <Icon icon={item.icon} size="sm" />
          {item.text}
        </li>
      ))}
    </ul>
  );
}

export const platformsText = (platforms: string[]) => platforms.map((p) => PLATFORM[p] ?? p).join(' + ');
const formatText = (format: string | null | undefined) =>
  format === 'carousel' ? 'Carousel' : format === 'single_image' ? 'Single image' : null;

/** Version 0, read-only. The same fields the new-task page collects, in the order they were asked. */
export function BriefPreview({ details }: { details: BriefDetails }) {
  switch (details.type) {
    case 'post':
      return (
        <div className="flex flex-col gap-(--mbm-space-4)">
          <CopyPanel label="Caption" tags={details.hashtags}>
            {details.caption}
          </CopyPanel>
          <FactRow
            items={[
              {
                icon: CalendarBlank,
                text: details.scheduledUploadAt ? when.format(new Date(details.scheduledUploadAt)) : null,
              },
              { icon: ImageSquare, text: formatText(details.format) },
              { icon: Sparkle, text: details.audioNote },
              { icon: LinkIcon, text: platformsText(details.platforms) },
            ]}
          />
        </div>
      );
    case 'reel':
      return (
        <div className="flex flex-col gap-(--mbm-space-4)">
          <CopyPanel label="Hook, script & shots">{details.script}</CopyPanel>
          <CopyPanel label="Caption" tags={details.hashtags}>
            {details.caption}
          </CopyPanel>
          <FactRow
            items={[
              {
                icon: CalendarBlank,
                text: details.scheduledUploadAt ? when.format(new Date(details.scheduledUploadAt)) : null,
              },
              { icon: ImageSquare, text: details.durationSeconds ? `${details.durationSeconds} sec` : null },
              { icon: Sparkle, text: details.audioNote },
              { icon: LinkIcon, text: platformsText(details.platforms) },
            ]}
          />
        </div>
      );
    case 'website':
      return (
        <dl className="flex flex-col gap-(--mbm-space-4)">
          <Maybe label="Goal & audience">{details.goalAudience}</Maybe>
          <Maybe label="Domain">{[details.domainName, details.domainRegistrar].filter(Boolean).join(' · ')}</Maybe>
          <Maybe label="Style">{details.style}</Maybe>
          <Maybe label="Brand colours">
            {[details.primaryColor, details.secondaryColor].filter(Boolean).join(' · ')}
          </Maybe>
          <Maybe label="Fonts">{details.fonts}</Maybe>
          {details.pages.length > 0 ? (
            <Field label="Pages & sections">
              <span className="flex flex-col gap-(--mbm-space-1)">
                {details.pages.map((page) => (
                  <span key={page.title}>
                    <strong className="text-label-large">{page.title}</strong>
                    {page.sections.length > 0 ? ` · ${page.sections.join(', ')}` : ''}
                  </span>
                ))}
              </span>
            </Field>
          ) : null}
          <Maybe label="Logo notes">{details.logoNotes}</Maybe>
          <Maybe label="General notes">{details.generalNotes}</Maybe>
          <Maybe label="Sites they like">{details.referenceSites.join('\n')}</Maybe>
          <Maybe label="Live by">{details.liveBy ? formatDayYear(`${details.liveBy}T00:00:00.000Z`) : null}</Maybe>
        </dl>
      );
    case 'monthly_update':
      return (
        <dl className="flex flex-col gap-(--mbm-space-4)">
          <Maybe label="Page">{details.pageUrl}</Maybe>
          <Maybe label="Kind of change">
            {CHANGE_LABEL[details.changeKind as (typeof CHANGE_KINDS)[number]] ?? details.changeKind}
          </Maybe>
          <Maybe label="What should change">{details.description}</Maybe>
          <Maybe label="Live by">{details.liveBy ? formatDay(`${details.liveBy}T00:00:00.000Z`) : null}</Maybe>
        </dl>
      );
    case 'custom':
      return (
        <dl className="flex flex-col gap-(--mbm-space-4)">
          <Maybe label="What's needed">{details.details}</Maybe>
        </dl>
      );
  }
}

/** The brief's files. Each opens through a short-lived signed link, never a permanent URL. */
export function BriefAssets({ assets }: { assets: BriefAsset[] }) {
  const toast = useToast();
  if (assets.length === 0) return null;

  const open = async (asset: BriefAsset) => {
    try {
      const res = await api.api.storage['presigned-download'].$post({ json: { key: asset.key } });
      if (!res.ok) throw await mapResponseError(res);
      const { downloadUrl } = await res.json();
      if (downloadUrl) window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch {
      toast({ title: 'Couldn’t open the file', description: asset.name, tone: 'error' });
    }
  };

  return (
    <div className="flex flex-col gap-(--mbm-space-2)">
      <span className="text-label-small text-text-tertiary uppercase">Brief assets</span>
      <ul className="flex flex-wrap gap-(--mbm-space-2)">
        {assets.map((asset) => (
          <li key={asset.id}>
            <button
              type="button"
              onClick={() => void open(asset)}
              className="inline-flex max-w-full items-center gap-(--mbm-space-2) rounded-full bg-surface-tint px-(--mbm-space-3) py-(--mbm-space-1) text-label-medium text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              <Icon icon={FileText} size="sm" />
              <span className="truncate">{asset.name}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
